import { useCallback, useEffect, useState } from "react";
import { getMeta, getFilters, getAnalytics } from "@/lib/procureApi";
import { UploadZone } from "@/components/procure/UploadZone";
import { SettingsPanel } from "@/components/procure/SettingsPanel";
import { DashboardExport } from "@/components/procure/DashboardExport";
import { LineLeaderboard } from "@/components/procure/LineLeaderboard";
import { FilterBar } from "@/components/procure/FilterBar";
import { KpiCards } from "@/components/procure/KpiCards";
import { TrendsChart } from "@/components/procure/TrendsChart";
import { FunnelFlow } from "@/components/procure/FunnelFlow";
import { AgingChart } from "@/components/procure/AgingChart";
import { TurnaroundLeaderboard } from "@/components/procure/TurnaroundLeaderboard";
import { BuyerTiles } from "@/components/procure/BuyerTiles";
import { Finder } from "@/components/procure/Finder";
import { TurnaroundHistory } from "@/components/procure/TurnaroundHistory";
import { BiggestMovers } from "@/components/procure/BiggestMovers";
import { StuckAlerts } from "@/components/procure/StuckAlerts";
import { OrdersTable } from "@/components/procure/OrdersTable";
import { Ref2Drawer } from "@/components/procure/Ref2Drawer";
import { SnapshotCompare } from "@/components/procure/SnapshotCompare";
import { Broadcast, Spinner, SlidersHorizontal } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { order_type: "", buyer: "", branch: "", supplier: "", status: "", date_from: "", date_to: "" };

export default function Dashboard() {
  const { user, signOut, isAdmin, isBuyer } = useAuth();
  const [meta, setMeta] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotId, setSnapshotId] = useState("");
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState(EMPTY);
  const [analytics, setAnalytics] = useState(null);
  const [ref2, setRef2] = useState(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [showCompare, setShowCompare] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [trendPeriod, setTrendPeriod] = useState("month");

  const refreshMeta = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const m = await getMeta();
      const has = m && m.count > 0;
      setMeta(has ? m : null);
      setSnapshots(has ? m.snapshots : []);
      if (has) setSnapshotId((prev) => (m.snapshots.some((s) => s.id === prev) ? prev : m.snapshots[0].id));
      return has;
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    refreshMeta();
  }, [refreshMeta]);

  const loaded = !!meta && !!snapshotId;

  useEffect(() => {
    if (!loaded) return;
    getFilters(snapshotId).then(setOptions);
  }, [loaded, snapshotId]);

  useEffect(() => {
    if (!loaded) return;
    getAnalytics({ ...filters, snapshot_id: snapshotId, trend_period: trendPeriod }).then(setAnalytics);
  }, [loaded, filters, snapshotId, trendPeriod, uploadKey]);

  const onUploaded = async (res) => {
    setFilters(EMPTY);
    const m = await getMeta();
    setMeta(m && m.count > 0 ? m : null);
    setSnapshots(m?.snapshots || []);
    if (res?.snapshot_id) setSnapshotId(res.snapshot_id);
    setUploadKey((k) => k + 1);
  };

  const currentSnap = snapshots.find((s) => s.id === snapshotId);
  const setBuyer = (b) => setFilters((f) => ({ ...f, buyer: b }));
  const scrollToTestId = (tid) => {
    const el = document.querySelector(`[data-testid="${tid}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen pf-grid-bg">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-[#E3E7EC] bg-[#FFFFFF] px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-[#CAD1DB] bg-[#FFFFFF]">
            <Broadcast size={20} weight="duotone" className="text-[#F0323E]" />
          </div>
          <div>
            <h1 className="text-base font-semibold tracking-tight text-[#0F172A]">ProcureFlow</h1>
            <p className="pf-label">Unprocessed Order Control Tower</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentSnap && (
            <div className="hidden text-right sm:block">
              <div className="pf-label">Snapshot</div>
              <div className="font-mono text-xs text-[#0F172A]">{currentSnap.reference_date}</div>
            </div>
          )}
          {loaded && <DashboardExport />}
          <button
            data-testid="open-settings"
            onClick={() => setAdminOpen(true)}
            className="flex items-center gap-2 rounded-sm border border-[#CAD1DB] bg-white px-3.5 py-2 text-sm font-medium text-[#0F172A] transition-colors hover:bg-[#EDEFF3]"
          >
            <SlidersHorizontal size={16} weight="bold" /> Settings
          </button>
        </div>
      </header>

      <SettingsPanel
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        loaded={loaded}
        snapshots={snapshots}
        snapshotId={snapshotId}
        setSnapshotId={setSnapshotId}
        onOpenCompare={() => setShowCompare(true)}
        currentSnap={currentSnap}
        meta={meta}
        isAdmin={isAdmin}
        isBuyer={isBuyer}
        onUploaded={onUploaded}
        user={user}
        signOut={signOut}
        onDriversChanged={() => setUploadKey((k) => k + 1)}
      />

      <main className="mx-auto max-w-[1600px] p-6">
        {loadingMeta ? (
          <div className="flex items-center justify-center gap-2 py-40 text-[#64748B]">
            <Spinner size={20} className="animate-spin" /> Loading…
          </div>
        ) : !loaded ? (
          <div className="mx-auto max-w-3xl pt-10">
            <div className="mb-6">
              <h2 className="text-4xl font-semibold tracking-tight text-[#0F172A] sm:text-5xl">
                Visibility of Unprocessed Orders
              </h2>
              <p className="mt-3 max-w-xl text-base text-[#64748B]">
                Upload your daily files — the <span className="font-medium text-[#0F172A]">Visibility Report</span> drives the dashboard, and the <span className="font-medium text-[#0F172A]">PO Query</span> powers the Finder's full <span className="font-mono text-[#F59E0B]">OR</span> → <span className="font-mono text-[#F0323E]">OQ</span> → <span className="font-mono text-[#8B5CF6]">OF</span> → OI/OX/OS/OJ chain. Reference 2 links every line across the flow. Either file can be uploaded on its own.
              </p>
            </div>
            {isAdmin ? (
              <UploadZone onUploaded={onUploaded} />
            ) : (
              <div className="border border-dashed border-[#E3E7EC] bg-white p-12 text-center text-sm text-[#64748B]" data-testid="readonly-empty">
                No data loaded yet. Please ask an admin to upload the Visibility Report.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <Finder snapshotId={snapshotId} onOpenRef2={(r) => setRef2(r)} />
            <FilterBar filters={filters} setFilters={setFilters} options={options} />
            <KpiCards summary={analytics?.summary} delta={analytics?.delta} onNavigate={scrollToTestId} />
            {!isBuyer && (
              <div className="grid grid-cols-12">
                <BuyerTiles
                  leaderboard={analytics?.buyer_lines || []}
                  activeBuyer={filters.buyer}
                  onSelectBuyer={setBuyer}
                />
              </div>
            )}
            {!isBuyer && (
              <div className="grid grid-cols-12">
                <LineLeaderboard snapshots={snapshots} refreshKey={uploadKey} />
              </div>
            )}
            <div className="grid grid-cols-12 gap-4">
              <TrendsChart counts={analytics?.trend_counts || []} period={trendPeriod} onPeriodChange={setTrendPeriod} />
              <FunnelFlow funnel={analytics?.funnel || []} />
            </div>
            <div className="grid grid-cols-12 gap-4">
              <AgingChart aging={analytics?.aging || []} />
              <TurnaroundLeaderboard
                leaderboard={analytics?.leaderboard || []}
                activeBuyer={filters.buyer}
                onSelectBuyer={setBuyer}
              />
            </div>
            <div className="grid grid-cols-12 gap-4">
              <TurnaroundHistory buyer={filters.buyer} refreshKey={uploadKey} />
              {!isBuyer && <BiggestMovers movers={analytics?.movers} onSelectBuyer={setBuyer} />}
            </div>
            <div className="grid grid-cols-12">
              <StuckAlerts
                snapshotId={snapshotId}
                filters={filters}
                onOpenRef2={(r) => setRef2(r)}
                refreshKey={uploadKey}
              />
            </div>
            <div className="grid grid-cols-12">
              <OrdersTable filters={filters} snapshotId={snapshotId} onOpenRef2={(r) => setRef2(r)} refreshKey={uploadKey} />
            </div>
          </div>
        )}
      </main>

      <Ref2Drawer ref2={ref2} snapshotId={snapshotId} open={!!ref2} onClose={() => setRef2(null)} />
      <SnapshotCompare open={showCompare} onClose={() => setShowCompare(false)} snapshots={snapshots} />
    </div>
  );
}
